import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchMyReviewPage } from '@/services/reviews';
import { useOrganization } from '@/hooks/useOrganization';
import { useSmartLoading } from '@/hooks/use-smart-loading';
import { EmptyState } from '@/components/ui/empty-state';
// Reuse the editor's OWN skeleton (from its own light module) during the
// resolve+redirect window so the public-page editor shows ONE continuous
// skeleton (no generic→editor swap) — without pulling the heavy editor into this
// resolver's chunk. The editor chunk is warmed via a preload below.
import { ReviewPageEditorSkeleton } from '@/components/review-editor/ReviewPageEditorSkeleton';

/**
 * MVP single-page model: there is exactly one review page per org.
 * This route just resolves it (creating a default if missing) and redirects
 * to the editor. The old multi-page list view is gone.
 */
export default function ReviewPages() {
  const { t } = useTranslation('reviews');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  // Editing the public review page is ORG_MANAGE, which a member does not hold.
  // Resolving the page for them only to redirect into an editor that 403s on
  // save is worse than not going there at all.
  const { isOrgAdmin, loading: orgLoading } = useOrganization();
  const barred = !orgLoading && !isOrgAdmin;
  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ['my-review-page'],
    queryFn: fetchMyReviewPage,
    enabled: !barred,
  });

  // Cover the resolve+redirect window with a skeleton. Keep this hook above the
  // early returns (Rules of Hooks; a prior React #310 crash came from hooks
  // after an early return).
  const showSkeleton = useSmartLoading(!barred && (orgLoading || isLoading || !data));

  // Warm the editor chunk while we resolve, so the redirect mounts it instantly
  // (the Outlet Suspense fallback is null, so an unwarmed chunk would blank).
  useEffect(() => {
    void import('./ReviewPageEditor');
  }, []);

  useEffect(() => {
    if (barred) {
      navigate('/reviews', { replace: true });
      return;
    }
    if (data?.id) {
      navigate(`/reviews/pages/${data.id}/edit`, { replace: true });
    }
  }, [barred, data, navigate]);

  if (isError) {
    // Was a bare red sentence floating on the page: no heading, no icon, no way
    // to act on it beyond the copy telling the user to reload by hand. Same
    // shape as every other "cannot show" in the app now, and the reload is a
    // button.
    return (
      <EmptyState
        kind="unavailable"
        title={t('pagesShell.loadFailedTitle')}
        description={t('pagesShell.loadFailed')}
        action={{ label: tCommon('actions.tryAgain'), onClick: () => refetch(), loading: isFetching }}
      />
    );
  }

  // showSkeleton FIRST so the ≥400ms hold isn't bypassed by the raw flag below.
  // Render the editor's OWN skeleton so it stays continuous through the redirect.
  if (showSkeleton) {
    return <ReviewPageEditorSkeleton />;
  }
  if (isLoading || !data) {
    return null;
  }

  return null;
}
